import Utils from "context/utils";
import { SESSION_STATUS, isSessionOpen, isSessionTransitioning } from "./sessionStatus";
import SessionObjectStore from "./objectStore";

// Page size for session-object listing (matches the backend ceiling/default; fewest round-trips).
const OBJECTS_PAGE_LIMIT = 1000;

// Session lifecycle events that carry a new status for the *current* session directly (the
// backend emits these with no payload -- the event type itself is the signal). session_deleted
// is handled separately below: the session no longer exists, so there's no status to set --
// it's cleared instead. session_deleting is a standalone signal and may arrive with no
// preceding session_closed; reopen_session emits no event (the acting client already has the
// new status from its own response), so there's no "reopened" entry here.
const SESSION_LIFECYCLE_EVENT_STATUS = {
  session_closing: SESSION_STATUS.CLOSING,
  session_closed: SESSION_STATUS.CLOSED,
  session_deleting: SESSION_STATUS.DELETING
};

// A collision is HTTP 400 with error="alias_in_use" in the body. The message is checked too so a
// body that carries only `message` still lands on the field rather than reading as a generic
// failure. Mirrors debugger/session/index.js#isAliasInUse.
function isUpdateSessionAliasInUse(error) {
  const data = error?.response?.data;
  if (error?.response?.status !== 400) {
    return false;
  }
  return data?.error === "alias_in_use" || /alias_in_use/.test(data?.message || "");
}

// Past this many remembered fingerprints, the oldest is forgotten first (see _rememberOwnGraphChange).
export const OWN_GRAPH_CHANGE_LIMIT = 1000;

// The four things the platform puts in a change, identical on the response and on its echo.
const graphChangeFingerprint = (alias, c) => `${alias}|${c.element_id}|${c.op}|${c.rev == null ? "" : c.rev}`;

export default class SessionManager {
  constructor(app, revision, updateProgress, onUpdate) {
    this.app = app;
    this.revision = revision;
    this.updateProgress = updateProgress;
    this.listeners = new Set();
    if (onUpdate) {
      this.listeners.add(onUpdate);
    }

    // Session state
    this.sessions = null;
    this.sessionId = null;
    this.session = null;
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    this.threadEventListeners = new Map();   // alias -> Set<callback>
    this.graphEventListeners = new Map();    // alias -> Set<callback>

    // A client's own graph writes, so their session_graph_changed echoes are not delivered as news.
    this._ownGraphChanges = new Set();           // fingerprints, insertion-ordered, capped
    this._graphWritesInFlight = new Map();       // alias -> count
    this._heldGraphEvents = new Map();           // alias -> payload[] waiting for a write to settle
    this._graphDoorEpoch = 0;                    // bumped by clearSessionState, even on a same-id reselect

    // Session objects: the index, the values somebody asked for, and how they stay current.
    this.objects = new SessionObjectStore({
      listPage: cursor => this.listSessionObjectsPage(cursor),
      download: alias => this.fetchSessionObject(alias),
      onChange: () => this.notifyListeners(),
    });

    // Sync state
    this.sessionSync = false;
    this.sessionSyncCounter = 0;
    // What is keeping sync alive, and why: widget key -> a phrase naming the live thing. See
    // `holdSync`. Empty is the ordinary case, and then the idle timeout below behaves as it always
    // did.
    this.syncHolds = new Map();
    // The person turned it OFF. A hold may keep sync running and may start it, but it may not
    // overrule this — otherwise the switch would not switch anything off.
    this.syncSuppressed = false;
    this.lastSessionId = null;

    // Timer for auto-refresh
    this.mounted = true;
    this.autoRefreshTimer = null;
  }

  destroy() {
    this.mounted = false;
    this.objects.dispose();
    if (this.autoRefreshTimer) {
      clearTimeout(this.autoRefreshTimer);
    }
  }

  /**
   * Subscriptions
   */

  addListener(callback) {
    this.listeners.add(callback);
  }

  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Fires every registered listener. `event` is optional: most call sites mean nothing more than
   * "something changed, re-render", and their listeners take no arguments. Lifecycle transitions
   * that a listener may need to act on specifically (currently only `{ type: "session_deleted" }`)
   * pass a descriptor through, which argument-less listeners simply ignore.
   */
  notifyListeners(event) {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (e) {
        console.error("Error notifying listener:", e);
      }
    }
  }

  /**
   * Thread event listeners (alias-scoped, fired on session_thread_item_posted events)
   */

  addThreadEventListener(alias, callback) {
    if (!this.threadEventListeners.has(alias)) {
      this.threadEventListeners.set(alias, new Set());
    }
    this.threadEventListeners.get(alias).add(callback);
  }

  removeThreadEventListener(alias, callback) {
    const set = this.threadEventListeners.get(alias);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) {
      this.threadEventListeners.delete(alias);
    }
  }

  notifyThreadEventListeners(alias, payload) {
    const set = this.threadEventListeners.get(alias);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error(`Error notifying thread listener for ${alias}:`, e);
      }
    }
  }

  /**
   * Graph event listeners (alias-scoped, fired on session_graph_changed events)
   */

  addGraphEventListener(alias, callback) {
    if (!this.graphEventListeners.has(alias)) {
      this.graphEventListeners.set(alias, new Set());
    }
    this.graphEventListeners.get(alias).add(callback);
  }

  removeGraphEventListener(alias, callback) {
    const set = this.graphEventListeners.get(alias);
    if (!set) return;
    set.delete(callback);
    if (set.size === 0) {
      this.graphEventListeners.delete(alias);
    }
  }

  notifyGraphEventListeners(alias, payload) {
    const set = this.graphEventListeners.get(alias);
    if (!set) return;
    for (const cb of set) {
      try {
        cb(payload);
      } catch (e) {
        console.error(`Error notifying graph listener for ${alias}:`, e);
      }
    }
  }

  /**
   * The one door for graph mutations. Remembers what the response says changed so the event the
   * platform posts for it is recognised as an echo and dropped; holds events for the alias while the
   * write is in flight, because the poll can see the echo before the response that identifies it.
   */
  async applyGraphMutations(alias, operations) {
    if (!this.sessionId) return Promise.reject(new Error("No session selected"));
    // A same-id reselect also bumps the epoch (session id alone can't see that as a change), so this
    // write can tell its own session apart from whatever clearSessionState reset next.
    // (`return` is avoided in `finally` below: a bare one there would override `return response`.)
    const epoch = this._graphDoorEpoch;
    this._graphWritesInFlight.set(alias, (this._graphWritesInFlight.get(alias) || 0) + 1);
    try {
      const response = await this.app.api.applySessionGraphMutations(
        this.revision.id, this.sessionId, alias, operations);
      if (epoch === this._graphDoorEpoch) {
        const changes = (response && response.data && response.data.changes) || [];
        for (const c of changes) this._rememberOwnGraphChange(graphChangeFingerprint(alias, c));
      }
      return response;
    } finally {
      if (epoch === this._graphDoorEpoch) {
        const left = (this._graphWritesInFlight.get(alias) || 1) - 1;
        if (left > 0) this._graphWritesInFlight.set(alias, left);
        else {
          this._graphWritesInFlight.delete(alias);
          const held = this._heldGraphEvents.get(alias) || [];
          this._heldGraphEvents.delete(alias);
          for (const payload of held) this._classifyGraphEvent(alias, payload);
        }
      }
    }
  }

  _rememberOwnGraphChange(fingerprint) {
    this._ownGraphChanges.delete(fingerprint);
    this._ownGraphChanges.add(fingerprint);
    if (this._ownGraphChanges.size > OWN_GRAPH_CHANGE_LIMIT) {
      this._ownGraphChanges.delete(this._ownGraphChanges.values().next().value);
    }
  }

  // Hold while a write for the alias is in flight; drop when every change is one of ours; else deliver.
  _classifyGraphEvent(alias, payload) {
    if (this._graphWritesInFlight.has(alias)) {
      if (!this._heldGraphEvents.has(alias)) this._heldGraphEvents.set(alias, []);
      this._heldGraphEvents.get(alias).push(payload);
      return;
    }
    const changes = payload.changes || [];
    const prints = changes.map(c => graphChangeFingerprint(alias, c));
    if (prints.length > 0 && prints.every(p => this._ownGraphChanges.has(p))) {
      for (const p of prints) this._ownGraphChanges.delete(p);
      return;
    }
    this.notifyGraphEventListeners(alias, payload);
  }

  /**
   * Data Access
   */

  /**
   * Whether the current session accepts mutations. Thin wrapper around
   * sessionStatus.isSessionOpen(session) so UI consumers (playground/index.js's
   * shellInterface) don't need to reach into `this.session` directly. The
   * session-events poller below (autoRefreshSessionEvents) checks
   * `isSessionOpen(this.session) || isSessionTransitioning(this.session)` inline
   * rather than calling this: the poller must keep running through "closing"/
   * "deleting" to observe the terminal lifecycle event, whereas mutations must
   * stay gated to strictly "open".
   */
  isSessionOpen() {
    return isSessionOpen(this.session);
  }

  /**
   * Actions
   */

  clearSessionState(session) {
    // Treat an empty/missing session as "no session selected": the store resets inactive (its
    // listStatus stays "idle"), so the details pane renders its no-session state — not the Objects
    // section — instead of getting stuck on a permanent "Loading session objects..." alert.
    const hasSession = session && session["id"];
    this.sessionId = hasSession ? session["id"] : null;
    this.session = hasSession ? session : null;
    this.objects.reset({ active: false });
    this.sessionEvents = null;
    this.sessionEventCreatedAt = null;
    // Drop stale thread/graph listeners; subscribers re-subscribe in componentDidUpdate.
    this.threadEventListeners = new Map();
    this.graphEventListeners = new Map();
    // The old session's writes are not this session's — forget them, and anything held for them.
    this._ownGraphChanges = new Set();
    this._graphWritesInFlight = new Map();
    this._heldGraphEvents = new Map();
    this._graphDoorEpoch += 1;   // unconditional, so a same-id reselect still invalidates writes in flight
    // A new session is a clean slate for sync too: the holds belong to what the widgets were
    // watching in the OLD session, and the suppression to a decision made about it. Selecting a
    // session turns sync off on the way in (playground/index.js), which is what sets suppression —
    // so this runs after it and undoes it, or the first session a person opens would be one where
    // no widget could ever ask for sync.
    this.syncHolds = new Map();
    this.syncSuppressed = false;
  }

  loadSessions(more) {
    this.updateProgress(true, more ? "Loading more sessions..." : "Loading sessions...", false, null);

    const body = {
      command: "list_sessions"
    };

    if (more) {
      body["start_session_id"] = this.lastSessionId;
    }

    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        let sessions = response.data.sessions;
        this.lastSessionId = response.data.last_session_id;

        if (more) {
          this.sessions = (this.sessions || []).concat(sessions);
        } else {
            this.sessions = sessions;
        }

        // Re-sync the currently-selected session (not just the list): the details pane reads
        // this.session, so without this it keeps showing a stale status after a lifecycle
        // transition until an unrelated poller update happens to touch it. Only update when the
        // freshly-loaded data actually includes it -- a paginated `more` load may not.
        if (this.session) {
          const refreshedSession = this.sessions.find(s => s["id"] === this.session["id"]);
          if (refreshedSession) {
            this.updateCurrentSession(refreshedSession);
          }
        }

        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();

        return this.sessions;
      }, error => {
        this.updateProgress(false, null, true, "Failed to load sessions: " + error.message);
      });
  }

  /** Lists the selected session's objects. Downloads none: a value is fetched when something asks for it. */
  loadSessionContent() {
    return this.objects.reset({ active: true });
  }

  listSessionObjectsPage(cursor) {
    const body = {
      "command": "list_session_objects",
      "session_id": this.sessionId,
      "limit": OBJECTS_PAGE_LIMIT
    };
    // OMIT a null cursor: the API types it as a string and answers 400 to `cursor: null`.
    if (cursor) body.cursor = cursor;
    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body).then(response => response.data);
  }

  fetchSessionObject(alias) {
    const body = {
      "command": "download_session_object",
      "session_id": this.sessionId,
      "alias": alias
    };
    return this.app.api.revisionsIdDataQueryPost(this.revision.id, body).then(
      response => response.data["object"],
      // The store reads `status`: 403 is "missing", by design of the platform, and anything else is a failure.
      error => Promise.reject({ status: error && error.response && error.response.status,
                                message: error && error.message })
    );
  }

  autoRefreshSessionEvents() {
    if (!this.mounted) return;

    this.autoRefreshTimer = setTimeout(() => {
      if (!this.mounted) return;

      // Sync winds itself down after three idle minutes, which is right when nothing is happening
      // and wrong the rest of the time: it used to stop mid-execution and mid-mission, leaving a
      // screen that looked live and was not. A hold says something IS happening, and holds the
      // clock at zero for as long as it does — so the three minutes start from the last live thing,
      // not from when the switch was flipped.
      if (this.sessionSync) {
          if (this.syncHolds.size > 0) {
              this.sessionSyncCounter = 0;
          } else if (this.sessionSyncCounter > 180) {
              this.stopSessionSync();
          } else {
              this.sessionSyncCounter++;
          }
      }

      // Poll through "open" AND the transitional statuses ("closing" / "deleting") so the
      // terminal lifecycle event (session_closed / session_deleted) the backend emits later is
      // actually observed -- otherwise the badge/actions get stuck at "closing"/"deleting" until
      // a manual refresh. Stop at "closed": it's a steady state for this client (nothing further
      // changes without a local user action), so there's no live event left to wait for. A
      // delete performed by *another* client against an already-"closed" session here won't be
      // seen live -- a deliberate trade to avoid polling every closed session forever.
      if (this.sessionSync && this.session && (isSessionOpen(this.session) || isSessionTransitioning(this.session))) {
          this.loadRecentSessionEvents();
      }

      this.autoRefreshSessionEvents();
    }, 1000);
  }

  /**
   * Keep sync alive while something is live — and start it if it is not running.
   *
   * A widget is the only thing that knows whether the screen it draws is waiting on something: an
   * execution in flight, cells the orchestrator will move. It says so here, under a key of its own,
   * and takes the hold back when the thing ends. `reason` is shown on the switch, so a person who
   * wonders why sync will not settle can read the answer rather than guess at it.
   *
   * A hold STARTS sync as well as holding it, because a hold means there is something to see and a
   * frozen screen is the failure being fixed. The one thing it cannot do is overrule the person: a
   * switch turned off stays off until it is turned back on (`syncSuppressed`).
   *
   * @param {string} key    — the widget's own id; one hold per widget
   * @param {string|null} reason — a short phrase naming what is live, or null/'' to release
   */
  holdSync(key, reason) {
    if (!key) return;
    const held = this.syncHolds.get(key) || null;
    const next = reason || null;
    if (held === next) return;

    if (next) {
      this.syncHolds.set(key, next);
    } else {
      this.syncHolds.delete(key);
    }

    // Starting it is a state change of its own; otherwise just tell the listeners what changed, so
    // the switch can say why it is on.
    if (next && !this.sessionSync && !this.syncSuppressed) {
      this.enableSessionSync(true);
      return;
    }
    this.sessionSyncCounter = 0;
    this.notifyListeners();
  }

  /** What is keeping sync on, in the order the holds were taken. Empty when nothing is. */
  syncHoldReasons() {
    return [...this.syncHolds.values()];
  }

  /**
   * Stop polling because nothing has happened for three minutes.
   *
   * Not `enableSessionSync(false)`: this is the clock's decision, not the person's, and it must not
   * leave sync suppressed — the next execution or the next mission that needs watching is entitled
   * to turn it back on.
   */
  stopSessionSync() {
    this.sessionSync = false;
    this.sessionSyncCounter = 0;
    this.notifyListeners();
  }

  /**
   * The switch, and every deliberate act that stands in for it.
   *
   * Turning it off SUPPRESSES: it is the person saying "stop", and a widget hold must not undo that
   * on the next tick. Turning it on lifts the suppression, which is why the chat widget calls this
   * when somebody presses Send — pressing a button that starts work is asking to watch it.
   */
  enableSessionSync(enable) {
    this.sessionSync = enable;
    this.syncSuppressed = !enable;
    this.sessionSyncCounter = 0;

    if (enable && this.sessionEventCreatedAt === null) {
      // First activation for this session — anchor the cursor at "now"
      // so we don't replay the entire session history on the next poll.
      // `sessionEventCreatedAt` is nulled in `clearSessionState` on
      // session change, so each new session re-anchors here.
      //
      // Subsequent OFF -> ON toggles intentionally DO NOT reset the
      // cursor: the next polling cycle picks up every session event
      // posted while sync was paused — session_object_modified,
      // session_object_deleted, AND session_thread_item_posted — and
      // dispatches them to listeners. That's how a thread widget
      // re-tails items posted by other actors during the sync gap.
      this.sessionEventCreatedAt = new Date().toISOString();
    }

    this.notifyListeners();
  }

  loadRecentSessionEvents() {
    let createdSince = this.sessionEventCreatedAt;
    this.loadSessionEvents(createdSince, true);
  }

  loadSessionEvents(createdSince, ascending) {
    const body = {
      "command": "list_session_events",
      "session_id": this.sessionId,
      "include_session": true,
      "ascending": ascending
    };

    if (createdSince) {
      body["created_since"] = createdSince;
    }

    const requestedSessionId = this.sessionId;

    this.app.api.revisionsIdDataQueryPost(this.revision.id, body)
      .then(response => {
        if (this.sessionId !== requestedSessionId) return;

        const session = response.data["session"];
        const sessionEvents = response.data["session_events"];

        this.session = session;
        this.sessionEvents = sessionEvents;

        if (sessionEvents.length > 0) {
            this.sessionEventCreatedAt = sessionEvents[sessionEvents.length - 1]["created_at"];
        }

        this.refreshSessionObjects(sessionEvents);
      }, error => {
        console.error("Failed to load session events: " + error.message);
      });
  }

  refreshSessionObjects(sessionEvents) {
    // Lifecycle events take precedence. session_deleted is terminal -- the session and
    // everything in it is gone, so short-circuit the rest of this batch (no point reconciling
    // objects/threads/graphs for a session that no longer exists) and defer to the dedicated
    // handler. The other three lifecycle events just update the current session's status.
    let statusChanged = false;
    for (const sessionEvent of sessionEvents) {
      const type = sessionEvent["type"];
      if ("session_deleted" === type) {
        this.handleSessionDeleted();
        return;
      }
      const status = SESSION_LIFECYCLE_EVENT_STATUS[type];
      if (status && this.session) {
        this.updateCurrentSession({ ...this.session, status });
        statusChanged = true;
      }
    }
    if (statusChanged) {
      this.notifyListeners();
    }

    this.objects.applyEvents(sessionEvents);

    // Dispatch thread events. The backend emits payload {session_object: {alias, item_id}}
    // for all three session-object event types (modified, deleted, thread_item_posted);
    // ExecutionEventsImpl.post_session_event merges that into the event row, so the
    // alias/item_id live under sessionEvent.session_object. We continue to pass a flat
    // {alias, item_id} shape to widget listeners so widgets/thread/index.js doesn't change.
    for (const sessionEvent of sessionEvents) {
      if ("session_thread_item_posted" === sessionEvent["type"]) {
        const so = sessionEvent["session_object"] || {};
        if (so.alias) {
          this.notifyThreadEventListeners(so.alias, { alias: so.alias, item_id: so.item_id });
        }
      } else if ("session_graph_changed" === sessionEvent["type"]) {
        const so = sessionEvent["session_object"] || {};
        if (so.alias) {
          this._classifyGraphEvent(so.alias, { alias: so.alias, changes: so.changes });
        }
      }
    }
  }

  createNewSession(session) {
    this.updateProgress(true, "Creating a new session...", false, null);

    const body = {
      command: "create_session"
    };

    if (session) {
      if (session.alias && session.alias.trim()) {
        body["alias"] = session.alias.trim();
      }
      if (session.description && session.description.trim()) {
        body["description"] = session.description.trim();
      }
      if (session.tracing) {
        body["tracing"] = session.tracing;
      }
    }

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        const newSession = response.data["session"];
        this.sessions = [newSession].concat(this.sessions || []);
        if (this.onUpdate) this.onUpdate();
        this.notifyListeners();
        return newSession;
      }, error => {
        this.updateProgress(false, null, true, "Failed to create new session: " + error.message);
      });
  }

  /**
   * Update
   *
   * Issues update_session for the current session and resolves the { ok, aliasInUse, message }
   * shape CreateSessionModal's Promise contract expects (see debugger/session/index.js#updateSession,
   * which this mirrors for the playground's Edit Session menu item). The alias is the one field
   * omitted when blank: the server grammar rejects "" and a blank field means "leave the alias
   * unchanged", not "clear it" -- description and tracing are always sent, since edit mode makes
   * every field editable.
   *
   * On success, the returned session is synced into both `this.session` and its matching entry in
   * `this.sessions` via updateCurrentSession -- the same session-sync path the lifecycle poller
   * uses -- so the playground selector and details rail reflect the change immediately rather than
   * showing the old alias until an unrelated update happens to touch it.
   *
   * Resolves rather than rejects: a 400 alias_in_use is about the value typed into the modal, so the
   * modal owns the message and this must not raise the shell-level error banner. Every other failure
   * (e.g. 409 session_not_open) resolves with the backend's own explanation for the modal to show as
   * a general error.
   */
  updateSession(fields) {
    this.updateProgress(true, "Updating session...", false, null);

    const body = {
      command: "update_session",
      session_id: this.sessionId,
      description: (fields.description || "").trim(),
      tracing: fields.tracing
    };
    const alias = (fields.alias || "").trim();
    if (alias) {
      body["alias"] = alias;
    }

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        const session = response.data["session"];
        this.updateCurrentSession(session);
        this.notifyListeners();
        return { ok: true };
      }, error => {
        this.updateProgress(false, null, false, null);
        if (isUpdateSessionAliasInUse(error)) {
          return { ok: false, aliasInUse: true };
        }
        // The backend's explanation for a refused update_session (e.g. "session_not_open") is in the
        // response body, not on the axios error's generic .message.
        return { ok: false, message: Utils.errorMessage(error) };
      });
  }

  /**
   * Session lifecycle
   *
   * The close/reopen/delete commands themselves are NOT issued from here: the only surface that
   * owns those actions is the debugger session view (debugger/session/index.js), which has no
   * SessionManager instance and dispatches them locally. What this manager does own is the
   * *reaction* to lifecycle changes observed on the session-events poller.
   */

  /**
   * Sets the current session and keeps the matching entry in `this.sessions[]` in sync, so
   * badges rendered from the list (debugger list, playground selector) reflect the change
   * immediately instead of showing stale status until an independent reload. Driven by the
   * session-events poller's lifecycle handling in refreshSessionObjects, which only knows the
   * new status and so merges it onto the current session before calling this.
   */
  updateCurrentSession(session) {
    this.session = session;
    if (this.sessions) {
      const index = this.sessions.findIndex(s => s["id"] === session["id"]);
      if (index !== -1) {
        this.sessions[index] = session;
      }
    }
  }

  /**
   * session_deleted: the session no longer exists server-side. Unselect it locally and refresh
   * the list so it disappears from the selector/list without a manual reload.
   *
   * The notification carries the event type because clearing our own session is not enough:
   * PlaygroundShell holds an independent copy of the selection (state.sessionId) and the URL still
   * names the deleted session. A generic "something changed" tick gives the shell no way to tell
   * this apart, so it would keep building URLs for a session that is gone.
   */
  handleSessionDeleted() {
    this.clearSessionState(null);
    this.notifyListeners({ type: "session_deleted" });
    this.loadSessions();
  }

  uploadSessionObject(alias, value) {
    this.updateProgress(true, "Updating session object...", false, null);

    const requestedSessionId = this.sessionId;
    const body = {
      command: "upload_session_object",
      session_id: this.sessionId,
      alias: alias,
      value: value
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        // The write happened in the session it was made in. If another is selected by now, that one's store must
        // not be handed this body.
        if (this.sessionId !== requestedSessionId) return;
        this.objects.applyLocalWrite(alias, value);
      }, error => {
        this.updateProgress(false, null, true, "Failed to update session object: " + error.message);
      });
  }

  deleteSessionObject(alias) {
    this.updateProgress(true, "Deleting session object...", false, null);

    const requestedSessionId = this.sessionId;
    const body = {
      command: "delete_session_object",
      session_id: this.sessionId,
      alias: alias
    };

    return this.app.api.revisionsIdDataCommandPost(this.revision.id, body)
      .then(response => {
        this.updateProgress(false, null, false, null);
        // The write happened in the session it was made in. If another is selected by now, that one's store must
        // not be handed this body.
        if (this.sessionId !== requestedSessionId) return;
        this.objects.applyLocalDelete(alias);
      }, error => {
        this.updateProgress(false, null, true, "Failed to delete session object: " + error.message);
      });
  }
}
